import React from "react";
import PropTypes from "prop-types";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Slider } from "primereact/slider";
import { InputSwitch } from "primereact/inputswitch";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { usePortalHostBody } from "~/context/PortalHostContext";
import { path, hexToRGBA } from "@chartLib/lib/utils";
import { MA_TYPES } from "@chartLib/lib/utils/getMACalculator";
import { SOURCE_PATH_TYPES } from "@chartLib/lib/utils/getSourcePath";
import {
  getCompleteSourceOptions,
  isSeriesSource,
} from "../utils/getAvailableSeries.js";
import { isPhaseEnabled } from "../config/featureFlags.js";

// Dropdown whose panel opens in the document the field actually lives in — the
// modal may be inside a popout window, where the module-level `document` is the
// main window's (issue #375). The host body comes from ModalBase via context.
const HostDropdown = ({ "data-evo-id": evoId, ...props }) => {
  const appendTo = usePortalHostBody();
  // evoId é repassado explicitamente: a regra evo-id/require-evo-id injeta um
  // data-evo-id="TODO" literal quando não vê o atributo, e ele venceria o spread.
  return <Dropdown {...props} appendTo={appendTo} data-evo-id={evoId} />;
};

HostDropdown.propTypes = {
  "data-evo-id": PropTypes.string,
};

// Memoized style objects to prevent recreation on every render
const DROPDOWN_VALUE_TEMPLATE_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  height: "100%",
  fontSize: "11px",
};

const BOOLEAN_CONTAINER_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "0.375rem",
};

const BOOLEAN_LABEL_STYLE = {
  fontSize: "0.75em",
};

// Additional style constants for ThresholdLineType and StrokeType
const FLEX_ROW_CONTAINER_STYLE = {
  display: "flex",
  flexDirection: "row",
  gap: "0.375rem",
  alignItems: "center",
  justifyContent: "flex-start",
};

const FLEX_ROW_GAP_SMALL_STYLE = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "0.2rem",
};

const FLEX_ALIGN_CENTER_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "0.2rem",
  fontSize: "11px",
};

const LABEL_STYLE = {
  fontSize: "0.7em",
  minWidth: "40px",
  textAlign: "right",
};

const COLOR_INPUT_STYLE = {
  width: "1.2rem",
  height: "1.2rem",
  border: "none",
  background: "none",
  padding: 0,
};

const SVG_INHERIT_COLOR_STYLE = { color: "inherit" };

const INPUT_NUMBER_NARROW_STYLE = { width: "55px" };
const DROPDOWN_WIDE_STYLE = { minWidth: "120px" };
const DROPDOWN_NARROW_STYLE = { minWidth: "70px" };

// Helper to resolve value from object, function, or path
const resolveValue = (target, name, defaultValue) => {
  if (typeof target === "function") {
    const v = target(name);
    return v !== undefined ? v : defaultValue;
  }
  const getValue = path(name);
  return getValue(target, defaultValue);
};

// Factory for window size type
export const WindowSizeType = ({
  name = "windowSize",
  displayName = "Período",
  min = 1,
  max = 1000,
  defaultValue = 10,
} = {}) => ({
  name,
  type: "number",
  displayName,
  min,
  max,
  defaultValue,
  render(target, onChange) {
    const value = resolveValue(target, name, defaultValue);
    return (
      <InputNumber
        value={value}
        onValueChange={(e) => onChange(name, e.value)}
        min={min}
        max={max}
        showButtons
        buttonLayout="horizontal"
        placeholder={displayName}
        step={1}
        data-evo-id="WindowSizeType-inputnumber"
      />
    );
  },
});

// Factory for source path type
// Options are dynamically sourced from the centralized source path handler
// NEW: Supports series-as-source with dynamic indicator options
export const SourcePathType = ({
  name = "sourcePath",
  displayName = "Campo",
  options = SOURCE_PATH_TYPES, // Single source of truth - centralized configuration!
  includeCalculated = true, // Set to false to show only basic OHLC fields
  allowSeriesSource = false, // NEW: Enable series-as-source dropdown options
  chartIndicators = null, // NEW: Chart indicators for building series options
  currentPanel = null, // NEW: Current panel ID for filtering
  excludeIndicatorId = null, // NEW: Exclude self when editing
  defaultValue = "close",
} = {}) => {
  return {
    name,
    type: "select",
    displayName,
    options, // Static options, will be overridden in render if needed
    defaultValue,
    render(target, onChange, chartContext = null) {
      const value = resolveValue(target, name, defaultValue);

      // Extract chart context if passed (from IndicatorEditModal)
      const contextIndicators =
        chartContext?.chartIndicators || chartIndicators;
      const contextPanel = chartContext?.currentPanel || currentPanel;
      const contextExcludeId =
        chartContext?.excludeIndicatorId || excludeIndicatorId;

      // Build dynamic options if series source is allowed
      let allOptions;
      if (allowSeriesSource && contextIndicators) {
        allOptions = getCompleteSourceOptions({
          includeCalculated,
          indicators: contextIndicators,
          currentPanel: contextPanel,
          excludeIndicatorId: contextExcludeId,
        });
      } else {
        // Backward compatible: use static OHLC options only
        allOptions = includeCalculated
          ? options
          : options.filter((opt) => opt.type === "basic");
      }

      // Check if current value is a series reference
      const valueIsSeriesRef = isSeriesSource(value);
      const calculationPhaseEnabled = isPhaseEnabled(
        "SERIES_AS_SOURCE",
        "CALCULATION",
      );

      // Show tooltip for disabled series options
      const getOptionTooltip = (option) => {
        if (option.category === "indicator" && option.disabled) {
          return "Série como fonte: em breve!";
        }
        return null;
      };

      return (
        <HostDropdown
          value={value}
          options={allOptions}
          onChange={(e) => {
            const selectedOption = allOptions.find(
              (opt) => opt.value === e.value,
            );

            // Block selection of series if calculation phase not enabled
            if (
              selectedOption?.category === "indicator" &&
              !calculationPhaseEnabled
            ) {
              // Show toast notification (will be added to handler if needed)
              console.log("Series as source: Coming soon!");
              return; // Don't update value
            }

            onChange(name, e.value);
          }}
          optionLabel="label"
          optionValue="value"
          optionDisabled="disabled"
          placeholder={displayName}
          baseZIndex={200000}
          tooltip={
            valueIsSeriesRef && !calculationPhaseEnabled
              ? "Aguardando ativação da feature"
              : null
          }
          tooltipOptions={{ position: "top" }}
          valueTemplate={(selected) => {
            const opt = allOptions.find(
              (o) => o.value === (selected?.value ?? selected),
            );
            if (!opt) return null;

            return (
              <div style={DROPDOWN_VALUE_TEMPLATE_STYLE}>
                <span>{opt.label}</span>
                {opt.category === "indicator" && opt.disabled && (
                  <span
                    style={{
                      marginLeft: "0.5rem",
                      fontSize: "0.75em",
                      opacity: 0.6,
                    }}
                  >
                    (em breve)
                  </span>
                )}
              </div>
            );
          }}
          itemTemplate={(option) => {
            if (option.category === "divider") {
              return (
                <div
                  style={{
                    borderTop: "1px solid var(--surface-border)",
                    margin: "0.25rem 0",
                    opacity: 0.3,
                  }}
                />
              );
            }

            return (
              <div
                style={DROPDOWN_VALUE_TEMPLATE_STYLE}
                title={getOptionTooltip(option)}
              >
                <span style={{ opacity: option.disabled ? 0.5 : 1 }}>
                  {option.label}
                </span>
                {option.category === "indicator" && option.disabled && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: "0.7em",
                      opacity: 0.5,
                      fontStyle: "italic",
                    }}
                  >
                    em breve
                  </span>
                )}
              </div>
            );
          }}
          data-evo-id="propertytypes-control-1"
        />
      );
    },
  };
};

// Factory for moving average type
// Options are dynamically sourced from the centralized MA handler
export const MovingAverageType = ({
  name = "movingAverageType",
  displayName = "Tipo de Média",
  options = MA_TYPES, // Single source of truth - no aliases needed!
  defaultValue = "sma",
} = {}) => ({
  name,
  type: "select",
  displayName,
  options,
  defaultValue,
  render(target, onChange) {
    const value = resolveValue(target, name, defaultValue);
    return (
      <HostDropdown
        value={value}
        options={options}
        onChange={(e) => onChange(name, e.value)}
        optionLabel="label"
        optionValue="value"
        placeholder={displayName}
        baseZIndex={200000}
        valueTemplate={(selected) => {
          const opt = options.find(
            (o) => o.value === (selected?.value ?? selected),
          );
          return opt ? (
            <div style={DROPDOWN_VALUE_TEMPLATE_STYLE}>
              <span>{opt.label}</span>
            </div>
          ) : null;
        }}
        data-evo-id="propertytypes-moving-avg-control"
      />
    );
  },
});

// Factory for volume path type
export const VolumePathType = ({
  name = "volumePath",
  displayName = "Volume",
  options = [
    { value: "volume", label: "Volume (Qtd)" }, // Traditional volume (shares/contracts)
    { value: "tickCount", label: "Volume (Neg)" }, // Number of trades per bar
    { value: "financialVolume", label: "Volume (Fin)" }, // Financial volume (monetary value)
  ],
  defaultValue = "volume",
} = {}) => ({
  name,
  type: "select",
  displayName,
  options,
  defaultValue,
  render(target, onChange) {
    const value = resolveValue(target, name, defaultValue);
    return (
      <HostDropdown
        value={value}
        options={options}
        onChange={(e) => onChange(name, e.value)}
        optionLabel="label"
        optionValue="value"
        placeholder={displayName}
        baseZIndex={200000}
        valueTemplate={(selected) => {
          const opt = options.find(
            (o) => o.value === (selected?.value ?? selected),
          );
          return opt ? (
            <div style={DROPDOWN_VALUE_TEMPLATE_STYLE}>
              <span>{opt.label}</span>
            </div>
          ) : null;
        }}
        data-evo-id="propertytypes-volume-path-control"
      />
    );
  },
});

// Boolean toggle property type for simple on/off options
export const BooleanType = ({
  name,
  displayName,
  defaultValue = true,
  trueLabel = "Sim",
  falseLabel = "Não",
} = {}) => ({
  name,
  type: "boolean",
  displayName,
  defaultValue,
  render(target, onChange) {
    const currentValue =
      typeof target === "boolean"
        ? target
        : resolveValue(target, name, defaultValue);

    return (
      <div style={BOOLEAN_CONTAINER_STYLE}>
        {falseLabel ? (
          <span style={BOOLEAN_LABEL_STYLE}>{falseLabel}</span>
        ) : null}
        <InputSwitch
          checked={!!currentValue}
          onChange={(e) => onChange(name, !!e.value)}
          data-evo-id="propertytypes-boolean-control"
        />
        {trueLabel ? (
          <span style={BOOLEAN_LABEL_STYLE}>{trueLabel}</span>
        ) : null}
      </div>
    );
  },
});

// Factory for generic select/dropdown type
export const SelectType = ({
  name,
  displayName,
  options = [],
  defaultValue,
} = {}) => ({
  name,
  type: "select",
  displayName,
  options,
  defaultValue,
  render(target, onChange) {
    const value = resolveValue(target, name, defaultValue);
    return (
      <HostDropdown
        value={value}
        options={options}
        onChange={(e) => onChange(name, e.value)}
        optionLabel="label"
        optionValue="value"
        placeholder={displayName}
        baseZIndex={200000}
        valueTemplate={(selected) => {
          const opt = options.find(
            (o) => o.value === (selected?.value ?? selected),
          );
          return opt ? (
            <div style={DROPDOWN_VALUE_TEMPLATE_STYLE}>
              <span>{opt.label}</span>
            </div>
          ) : null;
        }}
        data-evo-id="propertytypes-select-control"
      />
    );
  },
});

// Factory for multiplier type
export const MultiplierType = ({
  name = "multiplier",
  displayName = "Multiplicador",
  min = 0.1,
  max = 10,
  step = 0.1,
  defaultValue = 2,
} = {}) => ({
  name,
  type: "number",
  displayName,
  min,
  max,
  step,
  defaultValue,
  render(target, onChange) {
    const value = resolveValue(target, name, defaultValue);
    return (
      <InputNumber
        value={value}
        onValueChange={(e) => onChange(name, e.value)}
        min={min}
        max={max}
        showButtons
        buttonLayout="horizontal"
        placeholder={displayName}
        step={step}
        minFractionDigits={1}
        maxFractionDigits={2}
        data-evo-id="propertytypes-multiplier-control"
      />
    );
  },
});

// Generic numeric property type for indicators
export const NumericPropertyType = ({
  name,
  displayName,
  min = 0,
  max = 100,
  defaultValue = 0,
}) => ({
  name,
  type: "number",
  displayName,
  min,
  max,
  defaultValue,
  render(target, onChange) {
    const value = resolveValue(target, name, defaultValue);
    return (
      <InputNumber
        value={value}
        onValueChange={(e) => onChange(name, e.value)}
        min={min}
        max={max}
        showButtons
        buttonLayout="horizontal"
        placeholder={displayName}
        step={1}
        data-evo-id="propertytypes-numeric-control"
      />
    );
  },
});

/**
 * Deslocamento horizontal em BARRAS — o `DESLOCAR` do engine Silverlight
 * (`FMLMovingAverages.cs`, faixa −10000..10000) e o `Offset` do ChartIQ
 * (`studiesChartIQ.js:38`).
 *
 * A unidade está NO NOME de propósito: os outros "offset/shift" do repo são de
 * PREÇO — `shift` (Keltner), `levelOffset` (Darvas), `Shift Percentage`
 * (High Low) — e `displacement` já é do Ichimoku, valendo 26. Confundir os dois
 * transforma um envelope percentual num deslocamento temporal, calado.
 *
 * O deslocamento é aplicado genericamente em `applyIndicators` (via
 * `offsetSeries`): TODO indicador suporta a chave; só quem tinha o parâmetro no
 * legado a expõe na UI.
 */
export const OffsetBarsType = ({
  name = "offsetBars",
  displayName = "Deslocamento (barras)",
  min = -10000,
  max = 10000,
  defaultValue = 0,
} = {}) => NumericPropertyType({ name, displayName, min, max, defaultValue });

// Opacity slider property type (0-1 range with Slider instead of InputNumber)
export const OpacityType = ({
  name = "opacity",
  displayName = "Opacidade",
  defaultValue = 1,
} = {}) => ({
  name,
  type: "opacity",
  displayName,
  defaultValue,
  render(target, onChange) {
    // Modal passes the scalar value directly for scalar property types
    const value =
      typeof target === "number"
        ? target
        : resolveValue(target, name, defaultValue);
    const safeValue =
      typeof value === "number" && Number.isFinite(value)
        ? value
        : defaultValue;
    return (
      <div style={FLEX_ROW_GAP_SMALL_STYLE}>
        <Slider
          value={safeValue}
          min={0}
          max={1}
          step={0.01}
          style={{ width: "80px" }}
          onChange={(e) => onChange(name, e.value)}
        />
        <span
          style={{ width: "1.75rem", textAlign: "center", fontSize: "0.7em" }}
        >
          {safeValue.toFixed(2)}
        </span>
      </div>
    );
  },
});

// Numeric input with stroke properties for threshold lines (like RSI overbought/oversold)
export const ThresholdLineType = ({
  name,
  displayName,
  min = 0,
  max = 100,
  defaultValue = {
    value: 70,
    stroke: { color: "#B8C2CC", dash: "ShortDash", width: 1, opacity: 1 },
  },
} = {}) => {
  // Use exactly the same dash options as StrokeType for consistency
  const dashOptions = [
    {
      value: "Solid",
      label: "Contínua",
      template: (
        <svg width="30" height="8" style={SVG_INHERIT_COLOR_STYLE}>
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
    },
    {
      value: "ShortDash",
      label: "Curta",
      template: (
        <svg width="30" height="8" style={SVG_INHERIT_COLOR_STYLE}>
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="4,4"
          />
        </svg>
      ),
    },
    {
      value: "Dash",
      label: "Tracejada",
      template: (
        <svg width="30" height="8" style={SVG_INHERIT_COLOR_STYLE}>
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="6,4"
          />
        </svg>
      ),
    },
    {
      value: "Dot",
      label: "Pontilhada",
      template: (
        <svg width="30" height="8" style={SVG_INHERIT_COLOR_STYLE}>
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="2,6"
          />
        </svg>
      ),
    },
  ];

  // Use exactly the same strokeWidth options as StrokeType for consistency
  const strokeWidthOptions = [
    {
      value: 1,
      label: "1",
      template: (
        <svg width="30" height="8">
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="1"
          />
        </svg>
      ),
    },
    {
      value: 2,
      label: "2",
      template: (
        <svg width="30" height="8">
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
    },
    {
      value: 3,
      label: "3",
      template: (
        <svg width="30" height="8">
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="3"
          />
        </svg>
      ),
    },
    {
      value: 4,
      label: "4",
      template: (
        <svg width="30" height="8">
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="4"
          />
        </svg>
      ),
    },
    {
      value: 5,
      label: "5",
      template: (
        <svg width="30" height="8">
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="5"
          />
        </svg>
      ),
    },
  ];

  return {
    name,
    type: "thresholdLine",
    displayName,
    min,
    max,
    defaultValue,
    render(target, onChange) {
      const currentValue = resolveValue(target, name, defaultValue);
      const numericValue = currentValue?.value ?? defaultValue.value;
      const strokeValue = currentValue?.stroke ?? defaultValue.stroke;
      const strokeColor = strokeValue.color ?? defaultValue.stroke.color;
      const strokeDash = strokeValue.dash ?? defaultValue.stroke.dash;
      const strokeWidth = strokeValue.width ?? defaultValue.stroke.width;

      return (
        <div style={FLEX_ROW_CONTAINER_STYLE}>
          {/* Numeric Value Input - First element */}
          <div style={FLEX_ROW_GAP_SMALL_STYLE}>
            <label style={LABEL_STYLE}>Valor</label>
            <InputNumber
              value={numericValue}
              onValueChange={(e) =>
                onChange(name, { ...currentValue, value: e.value })
              }
              min={min}
              max={max}
              showButtons={false}
              placeholder={displayName}
              step={1}
              style={INPUT_NUMBER_NARROW_STYLE}
              data-evo-id="propertytypes-stroke-control-1"
            />
          </div>

          {/* Color - Exactly like StrokeType */}
          <div style={FLEX_ROW_GAP_SMALL_STYLE}>
            <label style={LABEL_STYLE}>Cor</label>
            {/* using copilot-instructions: use native color input instead of ColorPicker */}
            <input
              type="color"
              value={strokeColor}
              onChange={(e) =>
                onChange(name, {
                  ...currentValue,
                  stroke: { ...strokeValue, color: e.target.value },
                })
              }
              style={COLOR_INPUT_STYLE}
              data-evo-id="propertytypes-stroke-control-2"
            />
          </div>

          {/* Dash Style - Exactly like StrokeType */}
          <div style={FLEX_ROW_GAP_SMALL_STYLE}>
            <label style={LABEL_STYLE}>Traço</label>
            <HostDropdown
              value={strokeDash}
              options={dashOptions}
              onChange={(e) =>
                onChange(name, {
                  ...currentValue,
                  stroke: { ...strokeValue, dash: e.value },
                })
              }
              optionLabel="label"
              optionValue="value"
              placeholder="Traço"
              baseZIndex={200000}
              itemTemplate={(option) => (
                <div style={FLEX_ALIGN_CENTER_STYLE}>
                  {option.template}
                  <span>{option.label}</span>
                </div>
              )}
              valueTemplate={(selected) => {
                // using copilot-instructions: handle both object and string values, center vertically, left horizontally
                let opt;
                if (
                  selected &&
                  typeof selected === "object" &&
                  selected.value
                ) {
                  opt = dashOptions.find((o) => o.value === selected.value);
                } else {
                  opt = dashOptions.find((o) => o.value === selected);
                }
                return opt ? (
                  <div style={DROPDOWN_VALUE_TEMPLATE_STYLE}>
                    {opt.template}
                    <span>{opt.label}</span>
                  </div>
                ) : null;
              }}
              style={DROPDOWN_WIDE_STYLE}
              data-evo-id="propertytypes-stroke-control-3"
            />
          </div>

          {/* Width - Exactly like StrokeType */}
          <div style={FLEX_ROW_GAP_SMALL_STYLE}>
            <label style={LABEL_STYLE}>Espessura</label>
            <HostDropdown
              value={strokeWidth}
              options={strokeWidthOptions}
              onChange={(e) =>
                onChange(name, {
                  ...currentValue,
                  stroke: { ...strokeValue, width: e.value },
                })
              }
              optionLabel="label"
              optionValue="value"
              placeholder="Espessura"
              baseZIndex={200000}
              itemTemplate={(option) => (
                <div style={FLEX_ALIGN_CENTER_STYLE}>
                  {option.template}
                  <span>{option.label}</span>
                </div>
              )}
              valueTemplate={(selected) => {
                const opt = strokeWidthOptions.find(
                  (o) => o.value === (selected?.value ?? selected),
                );
                return opt ? (
                  <div style={DROPDOWN_VALUE_TEMPLATE_STYLE}>
                    {opt.template}
                    <span>{opt.label}</span>
                  </div>
                ) : null;
              }}
              style={DROPDOWN_NARROW_STYLE}
              data-evo-id="propertytypes-stroke-control-4"
            />
          </div>
        </div>
      );
    },
  };
};

// Text property type for text content editing
export const TextType = ({
  name = "text",
  displayName = "Texto",
  defaultValue = "Lorem ipsum...",
} = {}) => ({
  name,
  type: "text",
  displayName,
  defaultValue,
  render(target, onChange) {
    // Support two forms for `target`:
    // 1) an object (the full appearance/options object) where resolveValue(target, name) will find the value
    // 2) a direct primitive value (string) passed as `target` from callers that supply the value directly
    let value;
    if (target === undefined || target === null) {
      value = defaultValue;
    } else if (typeof target === "object" && !Array.isArray(target)) {
      value = resolveValue(target, name, defaultValue);
    } else {
      // treat target as direct value
      value = target;
    }

    // Ensure we always pass a defined string to avoid uncontrolled -> controlled warnings
    const safeValue =
      value === undefined || value === null ? "" : String(value);

    return (
      <InputText
        value={safeValue}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={displayName}
        style={{ width: "100%" }}
        data-evo-id="propertytypes-text-control"
      />
    );
  },
});

// Appearance property type for stroke
export const StrokeType = ({
  name = "stroke",
  displayName = "Cor da Linha",
  defaultValue = { color: "#2196F3", dash: "Solid", width: 1, opacity: 1 },
} = {}) => {
  // Dash options with SVG preview using currentColor for stroke
  const dashOptions = [
    {
      value: "Solid",
      label: "Contínua",
      template: (
        <svg width="30" height="8" style={SVG_INHERIT_COLOR_STYLE}>
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
    },
    {
      value: "ShortDash",
      label: "Curta",
      template: (
        <svg width="30" height="8" style={SVG_INHERIT_COLOR_STYLE}>
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="4,4"
          />
        </svg>
      ),
    },
    {
      value: "Dash",
      label: "Tracejada",
      template: (
        <svg width="30" height="8" style={SVG_INHERIT_COLOR_STYLE}>
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="6,4"
          />
        </svg>
      ),
    },
    {
      value: "Dot",
      label: "Pontilhada",
      template: (
        <svg width="30" height="8" style={SVG_INHERIT_COLOR_STYLE}>
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="2,6"
          />
        </svg>
      ),
    },
  ];
  // Add strokeWidth options with SVG preview
  const strokeWidthOptions = [
    {
      value: 1,
      label: "1",
      template: (
        <svg width="30" height="8">
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="1"
          />
        </svg>
      ),
    },
    {
      value: 2,
      label: "2",
      template: (
        <svg width="30" height="8">
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
    },
    {
      value: 3,
      label: "3",
      template: (
        <svg width="30" height="8">
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="3"
          />
        </svg>
      ),
    },
    {
      value: 4,
      label: "4",
      template: (
        <svg width="30" height="8">
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="4"
          />
        </svg>
      ),
    },
    {
      value: 5,
      label: "5",
      template: (
        <svg width="30" height="8">
          <line
            x1="3"
            y1="4"
            x2="27"
            y2="4"
            stroke="currentColor"
            strokeWidth="5"
          />
        </svg>
      ),
    },
  ];

  return {
    name,
    type: "stroke",
    displayName,
    defaultValue,
    render(value = {}, onChange) {
      const strokeColor = value.color ?? defaultValue.color;
      const strokeDash = value.dash ?? defaultValue.dash;
      const strokeWidth = value.width ?? defaultValue.width;
      return (
        <div style={FLEX_ROW_CONTAINER_STYLE}>
          <div style={FLEX_ROW_GAP_SMALL_STYLE}>
            <label style={LABEL_STYLE}>Cor</label>
            {/* using copilot-instructions: use native color input instead of ColorPicker */}
            <input
              type="color"
              value={strokeColor}
              onChange={(e) => onChange("color", e.target.value)}
              style={COLOR_INPUT_STYLE}
              data-evo-id="propertytypes-control-2"
            />
          </div>
          <div style={FLEX_ROW_GAP_SMALL_STYLE}>
            <label style={LABEL_STYLE}>Traço</label>
            <HostDropdown
              value={strokeDash}
              options={dashOptions}
              onChange={(e) => onChange("dash", e.value)}
              optionLabel="label"
              optionValue="value"
              placeholder="Traço"
              baseZIndex={200000}
              itemTemplate={(option) => (
                <div style={FLEX_ALIGN_CENTER_STYLE}>
                  {option.template}
                  <span>{option.label}</span>
                </div>
              )}
              valueTemplate={(selected) => {
                // using copilot-instructions: handle both object and string values, center vertically, left horizontally
                let opt;
                if (
                  selected &&
                  typeof selected === "object" &&
                  selected.value
                ) {
                  opt = dashOptions.find((o) => o.value === selected.value);
                } else {
                  opt = dashOptions.find((o) => o.value === selected);
                }
                return opt ? (
                  <div style={DROPDOWN_VALUE_TEMPLATE_STYLE}>
                    {opt.template}
                    <span>{opt.label}</span>
                  </div>
                ) : null;
              }}
              style={DROPDOWN_WIDE_STYLE}
              data-evo-id="propertytypes-control-3"
            />
          </div>
          <div style={FLEX_ROW_GAP_SMALL_STYLE}>
            <label style={LABEL_STYLE}>Espessura</label>
            <HostDropdown
              value={strokeWidth}
              options={strokeWidthOptions}
              onChange={(e) => onChange("width", e.value)}
              optionLabel="label"
              optionValue="value"
              placeholder="Espessura"
              baseZIndex={200000}
              itemTemplate={(option) => (
                <div style={FLEX_ALIGN_CENTER_STYLE}>
                  {option.template}
                  <span>{option.label}</span>
                </div>
              )}
              valueTemplate={(selected) => {
                const opt = strokeWidthOptions.find(
                  (o) => o.value === (selected?.value ?? selected),
                );
                return opt ? (
                  <div style={DROPDOWN_VALUE_TEMPLATE_STYLE}>
                    {opt.template}
                    <span>{opt.label}</span>
                  </div>
                ) : null;
              }}
              style={DROPDOWN_NARROW_STYLE}
              data-evo-id="propertytypes-control-4"
            />
          </div>
        </div>
      );
    },
  };
};

// Simplified color property type for appearance (color only, no opacity)
export const ColorType = ({
  name = "color",
  displayName = "Cor",
  defaultValue = { color: "#2196F3" },
} = {}) => ({
  name,
  type: "color",
  displayName,
  defaultValue,
  render(value = {}, onChange) {
    const valueColor = value.color ?? defaultValue.color;
    return (
      <div style={FLEX_ROW_CONTAINER_STYLE}>
        <div style={FLEX_ROW_GAP_SMALL_STYLE}>
          <label style={LABEL_STYLE}>Cor</label>
          {/* using copilot-instructions: use native color input instead of ColorPicker */}
          <input
            type="color"
            value={valueColor}
            onChange={(e) => onChange("color", e.target.value)}
            style={COLOR_INPUT_STYLE}
            data-evo-id="propertytypes-color-control"
          />
        </div>
      </div>
    );
  },
});

// Font property type: font family, color and size
export const FontType = ({
  name = "font",
  displayName = "Fonte",
  options = [
    {
      value: "Helvetica Neue, Helvetica, Arial, sans-serif",
      label: "Helvetica",
    },
    { value: "Arial, Helvetica, sans-serif", label: "Arial" },
    { value: "Times New Roman, Times, serif", label: "Times New Roman" },
    { value: "Courier New, Courier, monospace", label: "Courier New" },
    { value: "Georgia, serif", label: "Georgia" },
    { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
  ],
  defaultValue = {
    fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
    color: "#000000",
    fontSize: 12,
  },
} = {}) => ({
  name,
  type: "font",
  displayName,
  options,
  defaultValue,
  render(value = {}, onChange) {
    const fontFamily = value.fontFamily ?? defaultValue.fontFamily;
    const color = value.color ?? defaultValue.color;
    const fontSize = value.fontSize ?? defaultValue.fontSize;

    return (
      <div style={FLEX_ROW_CONTAINER_STYLE}>
        <div style={FLEX_ROW_GAP_SMALL_STYLE}>
          <label style={LABEL_STYLE}>Fonte</label>
          <HostDropdown
            value={fontFamily}
            options={options}
            onChange={(e) => onChange("fontFamily", e.value)}
            optionLabel="label"
            optionValue="value"
            placeholder="Fonte"
            baseZIndex={200000}
            style={{ minWidth: "130px" }}
            data-evo-id="propertytypes-font-control"
          />
        </div>

        <div style={FLEX_ROW_GAP_SMALL_STYLE}>
          <label style={LABEL_STYLE}>Cor</label>
          <input
            type="color"
            value={color}
            onChange={(e) => onChange("color", e.target.value)}
            style={COLOR_INPUT_STYLE}
            data-evo-id="propertytypes-control-5"
          />
        </div>

        <div style={FLEX_ROW_GAP_SMALL_STYLE}>
          <label style={LABEL_STYLE}>Tamanho</label>
          <InputNumber
            value={fontSize}
            onValueChange={(e) => onChange("fontSize", e.value)}
            min={6}
            max={72}
            buttonLayout="horizontal"
            placeholder="Tamanho"
            step={1}
            style={{ width: "6rem" }}
            data-evo-id="propertytypes-control-6"
          />
        </div>
      </div>
    );
  },
});

// Fill property type for appearance (fill color + opacity)
export const FillType = ({
  name = "fill",
  displayName = "Preenchimento",
  defaultValue = { color: "#2196F3", opacity: 1 },
} = {}) => ({
  name,
  type: "fill",
  displayName,
  defaultValue,
  render(value = {}, onChange) {
    const valueColor = value.color ?? defaultValue.color;
    const valueOpacity = value.opacity ?? defaultValue.opacity;
    return (
      <div style={FLEX_ROW_CONTAINER_STYLE}>
        <div style={FLEX_ROW_GAP_SMALL_STYLE}>
          <label style={LABEL_STYLE}>Cor</label>
          <div
            style={{
              position: "relative",
              width: "1.2rem",
              height: "1.2rem",
              borderRadius: "3px",
              border: `2px solid ${valueColor}`,
              background: hexToRGBA(valueColor, valueOpacity),
              boxShadow: "0 0 2px #aaa",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* using copilot-instructions: use native color input instead of ColorPicker */}
            <input
              type="color"
              value={valueColor}
              onChange={(e) => onChange("color", e.target.value)}
              style={{
                width: "100%",
                height: "100%",
                opacity: 1,
                position: "absolute",
                left: 0,
                top: 0,
                cursor: "pointer",
                border: "none",
                background: "none",
                padding: 0,
              }}
              data-evo-id="propertytypes-control-7"
            />
          </div>
        </div>
        <div style={{ ...FLEX_ROW_GAP_SMALL_STYLE, minWidth: "80px" }}>
          <label style={LABEL_STYLE}>Opacidade</label>
          <Slider
            value={valueOpacity}
            min={0}
            max={1}
            step={0.01}
            style={{ width: "45px" }}
            onChange={(e) => onChange("opacity", e.value)}
          />
          <span
            style={{ width: "1.75rem", textAlign: "center", fontSize: "0.7em" }}
          >
            {(valueOpacity ?? 1).toFixed(2)}
          </span>
        </div>
      </div>
    );
  },
});

// Bar property type for appearance (color, opacity, fillColor, width)
export const BarType = ({
  name = "bar",
  displayName = "Barra",
  defaultValue = {
    color: "#4682B4",
    fillColor: "#4682B4",
    opacity: 1,
    width: 1,
  },
} = {}) => ({
  name,
  type: "bar",
  displayName,
  defaultValue,
  render(value = {}, onChange) {
    const valueColor = value.color ?? defaultValue.color;
    const valueFillColor = value.fillColor ?? defaultValue.fillColor;
    const valueOpacity = value.opacity ?? defaultValue.opacity;
    const strokeWidthValue = value.width ?? defaultValue.width;
    return (
      <div style={FLEX_ROW_CONTAINER_STYLE}>
        <div style={FLEX_ROW_GAP_SMALL_STYLE}>
          <label style={LABEL_STYLE}>Borda</label>
          {/* using copilot-instructions: use native color input instead of ColorPicker */}
          <input
            type="color"
            value={valueColor}
            onChange={(e) => onChange("color", e.target.value)}
            style={COLOR_INPUT_STYLE}
            data-evo-id="propertytypes-control-8"
          />
        </div>
        <div style={FLEX_ROW_GAP_SMALL_STYLE}>
          <label style={LABEL_STYLE}>Preenchimento</label>
          <div
            style={{
              position: "relative",
              width: "1.2rem",
              height: "1.2rem",
              borderRadius: "3px",
              border: `2px solid ${valueFillColor}`,
              background: hexToRGBA(valueFillColor, valueOpacity),
              boxShadow: "0 0 2px #aaa",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* using copilot-instructions: use native color input instead of ColorPicker */}
            <input
              type="color"
              value={valueFillColor}
              onChange={(e) => onChange("fillColor", e.target.value)}
              style={{
                width: "100%",
                height: "100%",
                opacity: 1,
                position: "absolute",
                left: 0,
                top: 0,
                cursor: "pointer",
                border: "none",
                background: "none",
                padding: 0,
              }}
              data-evo-id="propertytypes-control-9"
            />
          </div>
        </div>
        <div style={FLEX_ROW_GAP_SMALL_STYLE}>
          <label style={LABEL_STYLE}>Espessura</label>
          <HostDropdown
            value={strokeWidthValue}
            options={[
              {
                value: 1,
                label: "1",
                template: (
                  <svg width="30" height="8">
                    <line
                      x1="3"
                      y1="4"
                      x2="27"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="1"
                    />
                  </svg>
                ),
              },
              {
                value: 2,
                label: "2",
                template: (
                  <svg width="30" height="8">
                    <line
                      x1="3"
                      y1="4"
                      x2="27"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                ),
              },
              {
                value: 4,
                label: "4",
                template: (
                  <svg width="30" height="8">
                    <line
                      x1="3"
                      y1="4"
                      x2="27"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                  </svg>
                ),
              },
              {
                value: 5,
                label: "5",
                template: (
                  <svg width="30" height="8">
                    <line
                      x1="3"
                      y1="4"
                      x2="27"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="5"
                    />
                  </svg>
                ),
              },
            ]}
            baseZIndex={200000}
            onChange={(e) => onChange("width", e.value)}
            optionLabel="label"
            optionValue="value"
            placeholder="Espessura"
            itemTemplate={(option) => (
              <div style={FLEX_ALIGN_CENTER_STYLE}>
                {option.template}
                <span>{option.label}</span>
              </div>
            )}
            valueTemplate={(selected) => {
              // using copilot-instructions: center vertically and left align horizontally (matches StrokeType)
              const opts = [1, 2, 4, 5].map((v) => ({
                value: v,
                label: String(v),
                template: (
                  <svg width="30" height="8">
                    <line
                      x1="3"
                      y1="4"
                      x2="27"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth={v}
                    />
                  </svg>
                ),
              }));
              const opt = opts.find(
                (o) => o.value === (selected?.value ?? selected),
              );
              return opt ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center", // vertical center
                    justifyContent: "flex-start", // horizontal left
                    gap: "0.3rem",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  {opt.template}
                  <span>{opt.label}</span>
                </div>
              ) : null;
            }}
            style={DROPDOWN_NARROW_STYLE}
            data-evo-id="propertytypes-stroke-control-5"
          />
        </div>
        <div style={{ ...FLEX_ROW_GAP_SMALL_STYLE, minWidth: "80px" }}>
          <label style={LABEL_STYLE}>Opacidade</label>
          <Slider
            value={valueOpacity}
            min={0}
            max={1}
            step={0.01}
            style={{ width: "45px" }}
            onChange={(e) => onChange("opacity", e.value)}
          />
          <span
            style={{ width: "1.75rem", textAlign: "center", fontSize: "0.7em" }}
          >
            {(valueOpacity ?? 1).toFixed(2)}
          </span>
        </div>
      </div>
    );
  },
});

// Histogram bar property type for appearance with increasing/decreasing colors (MACD, Awesome Oscillator, etc.)
// Colors from ChartIQ CSS: .stx_histogram_up (#8cc176) and .stx_histogram_down (#b82c0c)
export const HistogramBarType = ({
  name = "barDivergence",
  displayName = "Histograma",
  defaultValue = {
    increasingColor: "#8cc176",
    decreasingColor: "#b82c0c",
    opacity: 0.6,
    width: 1,
  },
} = {}) => ({
  name,
  type: "histogramBar",
  displayName,
  defaultValue,
  render(value = {}, onChange) {
    const increasingColor =
      value.increasingColor ?? defaultValue.increasingColor;
    const decreasingColor =
      value.decreasingColor ?? defaultValue.decreasingColor;
    const valueOpacity = value.opacity ?? defaultValue.opacity;
    const strokeWidthValue = value.width ?? defaultValue.width;
    return (
      <div style={{ ...FLEX_ROW_CONTAINER_STYLE, flexWrap: "wrap" }}>
        <div style={FLEX_ROW_GAP_SMALL_STYLE}>
          <label
            style={{ fontSize: "0.7em", minWidth: "48px", textAlign: "right" }}
          >
            Crescente
          </label>
          <div
            style={{
              position: "relative",
              width: "1.2rem",
              height: "1.2rem",
              borderRadius: "3px",
              border: `2px solid ${increasingColor}`,
              background: hexToRGBA(increasingColor, valueOpacity),
              boxShadow: "0 0 2px #aaa",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <input
              type="color"
              value={increasingColor}
              onChange={(e) => onChange("increasingColor", e.target.value)}
              style={{
                width: "100%",
                height: "100%",
                opacity: 1,
                position: "absolute",
                left: 0,
                top: 0,
                cursor: "pointer",
                border: "none",
                background: "none",
                padding: 0,
              }}
              data-evo-id="HistogramBarType-increasingColor"
            />
          </div>
        </div>
        <div style={FLEX_ROW_GAP_SMALL_STYLE}>
          <label
            style={{ fontSize: "0.7em", minWidth: "48px", textAlign: "right" }}
          >
            Decrescente
          </label>
          <div
            style={{
              position: "relative",
              width: "1.2rem",
              height: "1.2rem",
              borderRadius: "3px",
              border: `2px solid ${decreasingColor}`,
              background: hexToRGBA(decreasingColor, valueOpacity),
              boxShadow: "0 0 2px #aaa",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <input
              type="color"
              value={decreasingColor}
              onChange={(e) => onChange("decreasingColor", e.target.value)}
              style={{
                width: "100%",
                height: "100%",
                opacity: 1,
                position: "absolute",
                left: 0,
                top: 0,
                cursor: "pointer",
                border: "none",
                background: "none",
                padding: 0,
              }}
              data-evo-id="HistogramBarType-decreasingColor"
            />
          </div>
        </div>
        <div style={FLEX_ROW_GAP_SMALL_STYLE}>
          <label style={LABEL_STYLE}>Espessura</label>
          <HostDropdown
            value={strokeWidthValue}
            options={[
              {
                value: 1,
                label: "1",
                template: (
                  <svg width="30" height="8">
                    <line
                      x1="3"
                      y1="4"
                      x2="27"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="1"
                    />
                  </svg>
                ),
              },
              {
                value: 2,
                label: "2",
                template: (
                  <svg width="30" height="8">
                    <line
                      x1="3"
                      y1="4"
                      x2="27"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                ),
              },
              {
                value: 4,
                label: "4",
                template: (
                  <svg width="30" height="8">
                    <line
                      x1="3"
                      y1="4"
                      x2="27"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                  </svg>
                ),
              },
              {
                value: 5,
                label: "5",
                template: (
                  <svg width="30" height="8">
                    <line
                      x1="3"
                      y1="4"
                      x2="27"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="5"
                    />
                  </svg>
                ),
              },
            ]}
            baseZIndex={200000}
            onChange={(e) => onChange("width", e.value)}
            optionLabel="label"
            optionValue="value"
            placeholder="Espessura"
            itemTemplate={(option) => (
              <div style={FLEX_ALIGN_CENTER_STYLE}>
                {option.template}
                <span>{option.label}</span>
              </div>
            )}
            valueTemplate={(selected) => {
              const opts = [1, 2, 4, 5].map((v) => ({
                value: v,
                label: String(v),
                template: (
                  <svg width="30" height="8">
                    <line
                      x1="3"
                      y1="4"
                      x2="27"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth={v}
                    />
                  </svg>
                ),
              }));
              const opt = opts.find(
                (o) => o.value === (selected?.value ?? selected),
              );
              return opt ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "0.3rem",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  {opt.template}
                  <span>{opt.label}</span>
                </div>
              ) : null;
            }}
            style={DROPDOWN_NARROW_STYLE}
            data-evo-id="HistogramBarType-width"
          />
        </div>
        <div style={{ ...FLEX_ROW_GAP_SMALL_STYLE, minWidth: "80px" }}>
          <label style={LABEL_STYLE}>Opacidade</label>
          <Slider
            value={valueOpacity}
            min={0}
            max={1}
            step={0.01}
            style={{ width: "45px" }}
            onChange={(e) => onChange("opacity", e.value)}
          />
          <span
            style={{ width: "1.75rem", textAlign: "center", fontSize: "0.7em" }}
          >
            {(valueOpacity ?? 1).toFixed(2)}
          </span>
        </div>
      </div>
    );
  },
});

// Fibonacci levels sub-modal component
const FibLevelsEditor = ({ levels, onChange }) => {
  const appendTo = usePortalHostBody();
  const [visible, setVisible] = React.useState(false);
  const [localLevels, setLocalLevels] = React.useState(levels);
  const [customValue, setCustomValue] = React.useState("");

  const activeCount = levels.filter((l) => l.display !== false).length;

  const handleOpen = () => {
    setLocalLevels(levels);
    setCustomValue("");
    setVisible(true);
  };

  const handleSave = () => {
    onChange(null, localLevels);
    setVisible(false);
  };

  const handleToggle = (idx) => {
    setLocalLevels((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, display: !l.display } : l)),
    );
  };

  const handleRemove = (idx) => {
    setLocalLevels((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAdd = () => {
    const val = parseFloat(customValue);
    if (isNaN(val)) return;
    if (localLevels.some((l) => l.level === val)) return;
    setLocalLevels((prev) =>
      [...prev, { level: val, display: true, custom: true }].sort(
        (a, b) => b.level - a.level,
      ),
    );
    setCustomValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleAdd();
    }
  };

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        className="p-button-text p-button-sm"
        onClick={() => setVisible(false)}
        data-evo-id="propertytypes-control-10"
      />
      <Button
        label="Salvar"
        icon="pi pi-check"
        className="p-button-success p-button-sm"
        onClick={handleSave}
        data-evo-id="propertytypes-control-11"
      />
    </div>
  );

  return (
    <>
      <Button
        label={`${activeCount} de ${levels.length} niveis`}
        icon="pi pi-sliders-h"
        className="p-button-outlined p-button-sm"
        onClick={handleOpen}
        style={{ fontSize: "0.8em" }}
        data-evo-id="propertytypes-control-12"
      />
      <Dialog
        header="Niveis Fibonacci"
        visible={visible}
        onHide={() => setVisible(false)}
        footer={footer}
        style={{ width: "min(360px, 90vw)" }}
        modal
        appendTo={appendTo}
        baseZIndex={200000}
        focusOnShow={false}
        dismissableMask={false}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.35rem",
            maxHeight: "300px",
            overflowY: "auto",
            padding: "0.25rem 0",
          }}
        >
          {localLevels.map((lvl, idx) => (
            <div
              key={`${lvl.level}_${idx}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.2rem 0",
              }}
            >
              <Checkbox
                checked={lvl.display !== false}
                onChange={() => handleToggle(idx)}
              />
              <span style={{ fontSize: "0.9em", minWidth: "60px" }}>
                {lvl.level}%
              </span>
              {lvl.custom && (
                <Button
                  icon="pi pi-times"
                  className="p-button-text p-button-sm p-button-danger"
                  style={{
                    width: "1.5rem",
                    height: "1.5rem",
                    padding: 0,
                    marginLeft: "auto",
                  }}
                  onClick={() => handleRemove(idx)}
                  tooltip="Remover nível"
                  tooltipOptions={{ position: "top" }}
                  data-evo-id="propertytypes-control-13"
                />
              )}
            </div>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.375rem",
            marginTop: "0.75rem",
            paddingTop: "0.75rem",
            borderTop: "1px solid var(--surface-border, #dee2e6)",
          }}
        >
          <input
            type="number"
            step="0.1"
            placeholder="Ex: 78.6"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              width: "100px",
              padding: "0.35rem 0.5rem",
              fontSize: "0.9em",
              border: "1px solid var(--surface-border, #dee2e6)",
              borderRadius: "4px",
              background: "var(--surface-ground, #fff)",
            }}
            data-evo-id="propertytypes-control-14"
          />
          <Button
            icon="pi pi-plus"
            label="Adicionar"
            className="p-button-text p-button-sm"
            onClick={handleAdd}
            data-evo-id="propertytypes-control-15"
          />
        </div>
      </Dialog>
    </>
  );
};

export const FibLevelsType = ({
  name = "levels",
  displayName = "Niveis Fibonacci",
  defaultValue = [],
} = {}) => ({
  name,
  type: "fibLevels",
  displayName,
  defaultValue,
  render(value, onChange) {
    const levels = Array.isArray(value) ? value : defaultValue;
    return <FibLevelsEditor levels={levels} onChange={onChange} />;
  },
});

// Editor de linhas do Cálculo R/G — como o FibLevelsEditor, mas cada nível tem
// cor PRÓPRIA opcional (null = herda a Cor da Linha do objeto), espelhando o
// Split[i].LinePen ?? pen-base do Silverlight. Nível: múltiplo do risco
// (-1 = stop, 0 = entrada, 3 = alvo 3:1...). Qualquer linha pode ser removida.
const RiskLevelsEditor = ({ levels, onChange }) => {
  const appendTo = usePortalHostBody();
  const [visible, setVisible] = React.useState(false);
  const [localLevels, setLocalLevels] = React.useState(levels);
  const [customValue, setCustomValue] = React.useState("");

  const activeCount = levels.filter((l) => l.display !== false).length;

  const levelLabel = (lvl) =>
    lvl.level === -1
      ? "-1 (stop)"
      : lvl.level === 0
        ? "0 (entrada)"
        : `${lvl.level} : 1`;

  const handleOpen = () => {
    setLocalLevels(levels);
    setCustomValue("");
    setVisible(true);
  };

  const handleSave = () => {
    onChange(null, localLevels);
    setVisible(false);
  };

  const handleToggle = (idx) => {
    setLocalLevels((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, display: !l.display } : l)),
    );
  };

  const handleColor = (idx, color) => {
    setLocalLevels((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, color } : l)),
    );
  };

  const handleRemove = (idx) => {
    setLocalLevels((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAdd = () => {
    const val = parseFloat(customValue);
    if (isNaN(val)) return;
    if (localLevels.some((l) => l.level === val)) return;
    setLocalLevels((prev) =>
      [...prev, { level: val, display: true, color: null }].sort(
        (a, b) => a.level - b.level,
      ),
    );
    setCustomValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleAdd();
    }
  };

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        className="p-button-text p-button-sm"
        onClick={() => setVisible(false)}
        data-evo-id="propertytypes-risklevels-cancel"
      />
      <Button
        label="Salvar"
        icon="pi pi-check"
        className="p-button-success p-button-sm"
        onClick={handleSave}
        data-evo-id="propertytypes-risklevels-save"
      />
    </div>
  );

  return (
    <>
      <Button
        label={`${activeCount} de ${levels.length} linhas`}
        icon="pi pi-sliders-h"
        className="p-button-outlined p-button-sm"
        onClick={handleOpen}
        style={{ fontSize: "0.8em" }}
        data-evo-id="propertytypes-risklevels-open"
      />
      <Dialog
        header="Linhas do Cálculo R/G"
        visible={visible}
        onHide={() => setVisible(false)}
        footer={footer}
        style={{ width: "min(380px, 90vw)" }}
        modal
        appendTo={appendTo}
        baseZIndex={200000}
        focusOnShow={false}
        dismissableMask={false}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.35rem",
            maxHeight: "300px",
            overflowY: "auto",
            padding: "0.25rem 0",
          }}
        >
          {localLevels.map((lvl, idx) => (
            <div
              key={`${lvl.level}_${idx}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.2rem 0",
              }}
            >
              <Checkbox
                checked={lvl.display !== false}
                onChange={() => handleToggle(idx)}
              />
              <span style={{ fontSize: "0.9em", minWidth: "80px" }}>
                {levelLabel(lvl)}
              </span>
              {/* using copilot-instructions: use native color input instead of ColorPicker */}
              <input
                type="color"
                value={lvl.color || "#FFFFFF"}
                onChange={(e) => handleColor(idx, e.target.value)}
                title={
                  lvl.color ? "Cor da linha" : "Sem cor própria (herda a geral)"
                }
                style={{
                  width: "26px",
                  height: "20px",
                  padding: 0,
                  border: "1px solid var(--surface-border, #dee2e6)",
                  borderRadius: "3px",
                  background: "transparent",
                  opacity: lvl.color ? 1 : 0.45,
                  cursor: "pointer",
                }}
                data-evo-id="propertytypes-risklevels-color"
              />
              {lvl.color && (
                <Button
                  icon="pi pi-undo"
                  className="p-button-text p-button-sm"
                  style={{ width: "1.5rem", height: "1.5rem", padding: 0 }}
                  onClick={() => handleColor(idx, null)}
                  tooltip="Voltar a herdar a cor geral"
                  tooltipOptions={{ position: "top" }}
                  data-evo-id="propertytypes-risklevels-inherit"
                />
              )}
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm p-button-danger"
                style={{
                  width: "1.5rem",
                  height: "1.5rem",
                  padding: 0,
                  marginLeft: "auto",
                }}
                onClick={() => handleRemove(idx)}
                tooltip="Remover linha"
                tooltipOptions={{ position: "top" }}
                data-evo-id="propertytypes-risklevels-remove"
              />
            </div>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.375rem",
            marginTop: "0.75rem",
            paddingTop: "0.75rem",
            borderTop: "1px solid var(--surface-border, #dee2e6)",
          }}
        >
          <input
            type="number"
            step="0.5"
            placeholder="Ex: 2 (alvo 2:1)"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              width: "120px",
              padding: "0.35rem 0.5rem",
              fontSize: "0.9em",
              border: "1px solid var(--surface-border, #dee2e6)",
              borderRadius: "4px",
              background: "var(--surface-ground, #fff)",
            }}
            data-evo-id="propertytypes-risklevels-add-value"
          />
          <Button
            icon="pi pi-plus"
            label="Adicionar"
            className="p-button-text p-button-sm"
            onClick={handleAdd}
            data-evo-id="propertytypes-risklevels-add"
          />
        </div>
      </Dialog>
    </>
  );
};

RiskLevelsEditor.propTypes = {
  levels: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

export const RiskLevelsType = ({
  name = "levels",
  displayName = "Linhas",
  defaultValue = [],
} = {}) => ({
  name,
  type: "riskLevels",
  displayName,
  defaultValue,
  render(value, onChange) {
    const levels = Array.isArray(value) ? value : defaultValue;
    return <RiskLevelsEditor levels={levels} onChange={onChange} />;
  },
});
